import { Box } from '@mui/material';
import { ReactElement } from 'react';
import {
  DataTable,
  FunctionField,
  ListContextProvider,
  ReferenceField,
  ResourceContextProvider,
  TextField,
  useList,
  type SortPayload,
} from 'react-admin';
import type { ClicksByReferrersResource } from '../../../../Entities/ClicksByReferrer';
import { reportDataTableSx } from '../../../../Components/ReportsWidget/utils';

export interface ClicksByReferrerDataTableProps {
  data: ClicksByReferrersResource[];
  isPending?: boolean;
  sort?: SortPayload;
  startDateLabel: string;
  endDateLabel: string;
}

export default function ClicksByReferrerDataTable({
  data,
  isPending = false,
  sort,
  startDateLabel,
  endDateLabel,
}: ClicksByReferrerDataTableProps): ReactElement {
  const listContext = useList<ClicksByReferrersResource>({
    data,
    isPending,
    sort,
    perPage: Math.max(data.length, 1),
  });

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <ListContextProvider value={listContext}>
        <ResourceContextProvider value="clicks_by_referrers">
          <DataTable rowClick={false} bulkActionButtons={false} size="small" sx={reportDataTableSx}>
            <DataTable.Col label="Region">
              <ReferenceField
                source="relationships.region.data.id"
                reference="regions"
                link={false}
              >
                <TextField source="attributes.name" />
              </ReferenceField>
            </DataTable.Col>
            <DataTable.Col source="attributes.refname" label="Refname">
              <TextField source="attributes.refname" />
            </DataTable.Col>
            <DataTable.Col label="Start Date" disableSort>
              <FunctionField render={() => startDateLabel} />
            </DataTable.Col>
            <DataTable.Col label="End Date" disableSort>
              <FunctionField render={() => endDateLabel} />
            </DataTable.Col>
            <DataTable.NumberCol source="attributes.chargeable_clicks" label="Chargeable" />
            <DataTable.NumberCol source="attributes.non_chargeable_clicks" label="Non-Chargeable" />
            <DataTable.NumberCol
              source="attributes.cpc"
              label="CPC"
              options={{ style: 'currency', currency: 'AUD' }}
            />
            <DataTable.NumberCol
              source="attributes.gross_revenue"
              label="Gross Revenue"
              options={{ style: 'currency', currency: 'AUD' }}
            />
            <DataTable.NumberCol
              source="attributes.net_revenue"
              label="Net Revenue"
              options={{ style: 'currency', currency: 'AUD' }}
            />
          </DataTable>
        </ResourceContextProvider>
      </ListContextProvider>
    </Box>
  );
}
